import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AppButton from "../Common/Button/Button";
import AppIconButton from "../Common/Button/IconButton";
import FormInput from "../Common/FormInputs/MuiTextField";
import DataTable from "../Common/DataTable/DataTable";
import IncrementalPagination from "../Common/DataTable/IncrementalPagination";
import ScreenLoader from "../Common/ScreenLoader/ScreenLoader";
import EmptyState from "../Common/EmptyState/EmptyState";
import { actionButtonsRenderer, textRenderer } from "../Common/DataTable/CellRenderers";
import { SectionHeading } from "../Common/Typography/AppTypography";
import Alert from "../layout/Alert";
import ResponsiveContentWrapper from "../layout/ResponsiveContentWrapper";
import { setAlert } from "../../action/alert";
import {
  fetchAllAssetMetadata,
  getAssetErrorMessage,
} from "../../action/asset";
import AssetUploadForm from "./AssetUploadForm";
import AssetDeleteForm from "./AssetDeleteForm";

const assetPagePaperSx = {
  p: { xs: 1.5, sm: 2, md: 3 },
  boxShadow: 2,
  borderRadius: 1,
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  overflow: "visible",
};

const assetBackButtonSx = {
  color: "text.primary",
  border: "1px solid",
  borderColor: "rgba(0, 0, 0, 0.42)",
  bgcolor: "#fff",
  "&:hover": {
    color: "text.primary",
    bgcolor: "grey.100",
    borderColor: "text.primary",
  },
};

function AssetPageHeader({ title, onBack }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        mb: { xs: 2, md: 3 },
        minWidth: 0,
      }}
    >
      <AppIconButton
        variant="ghost"
        size="medium"
        tooltip="Back"
        aria-label="Back to assets"
        onClick={onBack}
        icon={<ArrowBackIcon fontSize="small" sx={{ color: "text.primary" }} />}
        sx={{ ...assetBackButtonSx, flexShrink: 0 }}
      />
      <SectionHeading sx={{ mb: 0, minWidth: 0 }}>{title}</SectionHeading>
    </Box>
  );
}

const upsertAsset = (assets, asset, { replaceMetadata = false } = {}) => {
  const assetId = asset?.assetId?.trim();

  if (!assetId) {
    return assets;
  }

  const nextAsset = {
    assetId,
    name: String(asset.name ?? "").trim(),
    description: String(asset.description ?? "").trim(),
  };
  const index = assets.findIndex((item) => item.assetId === assetId);

  if (index === -1) {
    return [nextAsset, ...assets];
  }

  const current = assets[index];
  const updated = [...assets];
  updated[index] = {
    ...current,
    assetId,
    name: replaceMetadata ? nextAsset.name : nextAsset.name || current.name || "",
    description: replaceMetadata
      ? nextAsset.description
      : nextAsset.description || current.description || "",
  };

  return updated;
};

const AssetManagement = ({ wrapperClass }) => {
  const dispatch = useDispatch();
  const [assets, setAssets] = useState([]);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [copiedAssetId, setCopiedAssetId] = useState("");
  const [createFormKey, setCreateFormKey] = useState(0);
  const [editAsset, setEditAsset] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [manualAssetId, setManualAssetId] = useState("");

  const loadAssets = useCallback(async () => {
    setLoading(true);

    try {
      const query = appliedSearch.trim();
      const assetIdSearch = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(query);
      let records = await fetchAllAssetMetadata({
        page: page + 1,
        pageSize,
        ...(query ? (assetIdSearch ? { assetId: query } : { name: query }) : {}),
      });

      if (query && !assetIdSearch && !records.length && !query.includes(" ")) {
        records = await fetchAllAssetMetadata({
          page: 1,
          pageSize,
          assetId: query,
        });
      }

      const nextAssets = Array.isArray(records) ? records : [];

      setAssets(nextAssets);
      setLoadError("");
      return { ok: true, assets: nextAssets };
    } catch (err) {
      const message = getAssetErrorMessage(
        err,
        "Unable to load assets.",
        "Unable to load assets. Asset metadata could not be found."
      );
      setLoadError(message);
      return { ok: false, assets: [] };
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedSearch]);

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  const filteredAssets = assets;

  const tableMessage =
    loadError && assets.length === 0
      ? "Asset metadata is not listed yet."
      : appliedSearch.trim()
        ? "No assets match this search."
        : "No assets found.";

  const openCreate = () => {
    setEditAsset(null);
    setCreateFormKey((key) => key + 1);
    setCreateOpen(true);
  };

  const closeCreate = () => {
    setCreateOpen(false);
  };

  const copyAssetId = async (assetId) => {
    try {
      await navigator.clipboard.writeText(assetId);
      setCopiedAssetId(assetId);
      setTimeout(() => {
        setCopiedAssetId((current) => (current === assetId ? "" : current));
      }, 2000);
    } catch {
      dispatch(setAlert("Failed to copy Asset ID", "danger"));
    }
  };

  const openEdit = (record) => {
    setCreateOpen(false);
    setEditAsset(record);
  };

  const closeEdit = () => {
    setEditAsset(null);
  };

  const openAssetById = (action) => {
    const assetId = manualAssetId.trim();

    if (!assetId) {
      dispatch(setAlert("Asset ID Cannot be Empty", "danger"));
      return;
    }

    const record = assets.find((item) => item.assetId === assetId) || {
      assetId,
      name: "",
      description: "",
    };

    if (action === "edit") {
      openEdit(record);
      return;
    }

    setDeleteTarget(record);
  };

  const handleFormSuccess = async (result) => {
    if (!result) {
      return;
    }

    if (result.type === "file" || result.type === "metadata") {
      setEditAsset(null);
      await loadAssets();
      return;
    }

    if (result.type === "create") {
      setCreateOpen(false);
      const loaded = await loadAssets();

      if (!result.assetId) {
        return;
      }

      if (loaded.ok && loaded.assets.some((item) => item.assetId === result.assetId)) {
        return;
      }

      setAssets((current) => upsertAsset(current, result));
    }
  };

  const handleDeleted = async ({ assetId, fullDelete }) => {
    setDeleteTarget(null);

    if (fullDelete) {
      setAssets((current) => current.filter((item) => item.assetId !== assetId));
    }

    const loaded = await loadAssets();

    if (!loaded.ok && fullDelete) {
      setAssets((current) => current.filter((item) => item.assetId !== assetId));
    }
  };

  const columns = [
    { key: "name", label: "Asset Name", minWidth: 180, render: textRenderer },
    { key: "description", label: "Description", minWidth: 220, render: textRenderer },
    {
      key: "assetId",
      label: "Asset ID",
      minWidth: 260,
      render: (value) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
          <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
            {value || "-"}
          </Typography>
          {value ? (
            <AppIconButton
              size="small"
              variant="ghost"
              tooltip="Copy Asset ID"
              aria-label="Copy Asset ID"
              onClick={(event) => {
                event.stopPropagation();
                copyAssetId(value);
              }}
              icon={
                copiedAssetId === value ? (
                  <CheckCircleOutlineIcon fontSize="small" />
                ) : (
                  <FileCopyIcon fontSize="small" />
                )
              }
            />
          ) : null}
        </Box>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      align: "center",
      justifyContent: "center",
      width: 112,
      minWidth: 112,
      cellSx: {
        verticalAlign: "middle",
        textAlign: "center",
      },
      render: actionButtonsRenderer([
        {
          id: "edit",
          icon: EditIcon,
          tooltip: "Edit",
          variant: "primary",
          onClick: (row) => openEdit(row),
        },
        {
          id: "delete",
          icon: DeleteIcon,
          tooltip: "Delete",
          variant: "danger",
          onClick: (row) => setDeleteTarget(row),
        },
      ]),
    },
  ];

  if (createOpen) {
    return (
      <ResponsiveContentWrapper className={wrapperClass || ""}>
        <Paper sx={assetPagePaperSx}>
          <Alert />
          <AssetPageHeader title="Create Asset" onBack={closeCreate} />
          <AssetUploadForm
            key={createFormKey}
            mode="create"
            embedded
            page
            onCancel={closeCreate}
            onSuccess={handleFormSuccess}
          />
        </Paper>
      </ResponsiveContentWrapper>
    );
  }

  if (editAsset) {
    return (
      <ResponsiveContentWrapper className={wrapperClass || ""}>
        <Paper sx={assetPagePaperSx}>
          <Alert />
          <AssetPageHeader title="Update Asset" onBack={closeEdit} />
          <AssetUploadForm
            key={editAsset.assetId}
            mode="update"
            embedded
            page
            initialValues={editAsset}
            onCancel={closeEdit}
            onSuccess={handleFormSuccess}
          />
        </Paper>
      </ResponsiveContentWrapper>
    );
  }

  return (
    <ResponsiveContentWrapper className={wrapperClass || ""}>
      <Paper
        sx={{
          p: { xs: 1.5, sm: 2 },
          boxShadow: 2,
          borderRadius: 1,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
        }}
      >
        <Alert />
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            gap: 1.5,
            mb: 2,
            minWidth: 0,
          }}
        >
          <SectionHeading sx={{ mb: 0 }}>Assets</SectionHeading>
          <AppButton
            variant="primary"
            size="medium"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Create Asset
          </AppButton>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
            minWidth: 0,
            width: "100%",
          }}
        >
          <Box sx={{ width: { xs: "100%", sm: 280 }, maxWidth: { xs: "100%", sm: 280 }, minWidth: 0 }}>
            <FormInput
              type="text"
              size="small"
              fullWidth
              placeholder="Search by asset name or Asset ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  setAppliedSearch(search.trim());
                  setPage(0);
                }
              }}
              endAdornment={
                search ? (
                  <IconButton
                    size="small"
                    edge="end"
                    aria-label="Clear search"
                    onClick={() => {
                      setSearch("");
                      setAppliedSearch("");
                      setPage(0);
                    }}
                    sx={{ p: 0.25 }}
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                ) : null
              }
              sx={{ width: "100%", maxWidth: "100%" }}
            />
          </Box>
          <AppButton
            variant="primary"
            size="medium"
            startIcon={<SearchIcon />}
            onClick={() => {
              setAppliedSearch(search.trim());
              setPage(0);
            }}
          >
            Search
          </AppButton>
        </Box>

        {!loading && loadError && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>
              {loadError} You can still open an existing asset by Asset ID.
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                gap: 1.5,
                flexWrap: "wrap",
                minWidth: 0,
                width: "100%",
                "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } },
              }}
            >
              <Box sx={{ width: { xs: "100%", sm: 360 }, maxWidth: "100%", minWidth: 0 }}>
                <FormInput
                  type="text"
                  size="small"
                  fullWidth
                  label="Asset ID"
                  placeholder="e.g. home-banner-test"
                  value={manualAssetId}
                  onChange={(e) => setManualAssetId(e.target.value)}
                  sx={{ width: "100%", maxWidth: "100%" }}
                />
              </Box>
              <AppButton
                variant="ghost"
                size="medium"
                onClick={() => openAssetById("edit")}
              >
                Edit
              </AppButton>
              <AppButton
                variant="ghost"
                size="medium"
                onClick={() => openAssetById("delete")}
              >
                Delete
              </AppButton>
            </Box>
          </Box>
        )}

        {loading ? (
          <ScreenLoader
            height={220}
            message="Loading assets..."
            sx={{ bgcolor: "transparent" }}
          />
        ) : !filteredAssets.length ? (
          <EmptyState
            height={220}
            message={tableMessage}
            sx={{ bgcolor: "transparent" }}
          />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={filteredAssets}
              rowKey="assetId"
              fullWidth
              minWidth="100%"
              msgForDisplay={tableMessage}
            />
            <IncrementalPagination
              page={page}
              pageSize={pageSize}
              dataLength={assets.length}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(0);
              }}
              pageSizeOptions={[20, 50, 100]}
            />
          </>
        )}
      </Paper>

      <AssetDeleteForm
        open={Boolean(deleteTarget)}
        assetId={deleteTarget?.assetId || ""}
        tags={deleteTarget?.tags}
        onClose={() => setDeleteTarget(null)}
        onDeleted={handleDeleted}
      />
    </ResponsiveContentWrapper>
  );
};

export default AssetManagement;
